import dayjs from 'dayjs';
import { useMemo } from 'react';

import { formatDate } from 'utils/dayjs';

import type { GetCollectionStatsOrdersStatusCountsReportsParams } from 'api';
import { useGetCollectionStatsOrdersStatusCountsReports } from 'api';

import { useOrganisationId } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useOrgNavigate } from 'hooks/useOrgNavigate';

import { adaptOrderResponse } from '../adapters/orderStats.adapter';
import {
  ORDER_DOT_COLOR,
  STATUS_WIDGET_ERROR_MESSAGE,
  STATUS_WIDGET_ORDER_ARIA_LABEL,
  STATUS_WIDGET_ORDER_TEST_ID_PREFIX,
  STATUS_WIDGET_ORDER_TITLE
} from '../constants';
import { StatusWidget } from '../StatusWidget';

export const WidgetOrderStatusesContainer = () => {
  const orgNavigate = useOrgNavigate();
  const organisationId = useOrganisationId<number>();
  const { filters } = useGlobalFilters();

  // we currently only use last 30 days, so using filters is misleading
  const dateRange =
    useMemo<GetCollectionStatsOrdersStatusCountsReportsParams>(() => {
      const queryParams: GetCollectionStatsOrdersStatusCountsReportsParams = {
        created_after: dayjs().subtract(30, 'day').format('YYYY-MM-DD')
      };
      if (filters?.dateRange?.from) {
        queryParams.created_after = formatDate(
          filters.dateRange.from,
          'utc',
          'iso'
        );
      }
      if (filters?.dateRange?.to) {
        queryParams.created_before = formatDate(
          filters.dateRange.to,
          'utc',
          'iso'
        );
      }
      return queryParams;
    }, [filters.dateRange]);

  const { data, isLoading, error } =
    useGetCollectionStatsOrdersStatusCountsReports(organisationId, dateRange, {
      query: { select: adaptOrderResponse }
    });

  const onBadgeClick = (status?: string) => {
    if (status) {
      orgNavigate(`/collections/orders?status=${status}`);
    } else {
      orgNavigate('/collections/orders');
    }
  };

  return (
    <StatusWidget
      ariaLabel={STATUS_WIDGET_ORDER_ARIA_LABEL}
      titleText={STATUS_WIDGET_ORDER_TITLE}
      headerLinkTo="/collections/orders"
      totalCount={data?.totalCount ?? 0}
      statusItems={data?.statusItems ?? []}
      onBadgeClick={onBadgeClick}
      testIdPrefix={STATUS_WIDGET_ORDER_TEST_ID_PREFIX}
      dotColorMap={ORDER_DOT_COLOR}
      loading={isLoading}
      error={error}
      errorMessage={STATUS_WIDGET_ERROR_MESSAGE}
      footer={null}
    />
  );
};
